import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { DeliveryDining, AddCircleOutline } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  createOnlineOrder,
  ONLINE_PLATFORMS,
} from "../../Store/Slices/restaurantSlice";
import styles from "./OnlineOrderButton.module.css";

const PLATFORM_META = {
  Talabat: { color: "#ff5a00", label: "T" },
  Zomato: { color: "#e23744", label: "Z" },
  Careem: { color: "#4bb543", label: "C" },
  Deliveroo: { color: "#00ccbc", label: "D" },
  "Noon Food": { color: "#feee00", label: "N", textColor: "#222" },
  Other: { color: "#64748b", label: "•" },
};

const OnlineOrderButton = ({ sx }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState(ONLINE_PLATFORMS[0]);
  const [orderNo, setOrderNo] = useState("");

  const openDialog = () => {
    setPlatform(ONLINE_PLATFORMS[0]);
    setOrderNo("");
    setOpen(true);
  };

  const handleCreate = () => {
    if (!orderNo.trim()) {
      toast.error("Enter the online order number", {
        position: "bottom-right",
      });
      return;
    }
    dispatch(createOnlineOrder({ platform, orderNo: orderNo.trim() }));
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        size="large"
        startIcon={<DeliveryDining sx={{ fontSize: "1.6rem !important" }} />}
        onClick={openDialog}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          fontSize: "1.05rem",
          px: 2.5,
          py: 1.2,
          borderRadius: 2,
          backgroundColor: "#0f766e",
          "&:hover": { backgroundColor: "#0b5952" },
          ...sx,
        }}
      >
        Online / Delivery
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: styles.paper, sx: { p: 3, width: "100%", maxWidth: 1000 } }}
      >
        <DialogTitle sx={{ fontSize: "1.9rem", fontWeight: 700 }}>
          New Online / Delivery Order
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
            Platform
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            {ONLINE_PLATFORMS.map((p) => {
              const meta = PLATFORM_META[p] || PLATFORM_META.Other;
              const selected = platform === p;

              return (
                <Box
                  key={p}
                  onClick={() => setPlatform(p)}
                  className={selected ? styles.platformCardSelected : styles.platformCard}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: selected ? "#0f766e" : "#e2e8f0",
                    backgroundColor: selected ? "#f0fdfa" : "#fff",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Radio
                    checked={selected}
                    sx={{ p: 0, "&.Mui-checked": { color: "#0f766e" } }}
                  />
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: meta.color,
                      color: meta.textColor || "#fff",
                      fontSize: "1.6rem",
                      fontWeight: 700,
                    }}
                  >
                    {meta.label}
                  </Avatar>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                    {p}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <TextField
            fullWidth
            margin="normal"
            label="Order Number"
            value={orderNo}
            onChange={(e) => setOrderNo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            autoFocus
            inputProps={{
              style: {
                fontSize: "1.8rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={handleCreate}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#0f766e",
              "&:hover": { backgroundColor: "#0b5952" },
            }}
          >
            Add Items
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OnlineOrderButton;
